/*
 * The order book.
 *
 * Zerodha's rejection message is shown verbatim and in full. "Insufficient
 * funds", "Circuit limit breached", "RMS: blocked for this security" — each of
 * those is the complete answer to why an order did not go through, and
 * paraphrasing it into "order rejected" throws that answer away.
 *
 * A paper order is marked as such rather than hidden. Seeing the two side by
 * side is what makes it obvious when a strategy is still on paper.
 */

import { useState } from "react";

import { useOrders, useStatus } from "../api/hooks";
import { ORDER_CAPTION, clockFromMs, price as fmtPrice, rupees } from "../lib/format";

type Filter = "all" | "live" | "paper" | "problems";

export function OrdersPage() {
  const status = useStatus();
  const orders = useOrders(200, status.data?.marketOpen);
  const [filter, setFilter] = useState<Filter>("all");

  const all = orders.data ?? [];
  const rows = all.filter((o) => {
    if (filter === "live") return o.mode === "live";
    if (filter === "paper") return o.mode === "paper";
    if (filter === "problems")
      return ["REJECTED", "REJECTED_LOCAL", "CANCELLED"].includes(o.status);
    return true;
  });

  const problems = all.filter((o) =>
    ["REJECTED", "REJECTED_LOCAL"].includes(o.status),
  ).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Orders</h1>
          <p>
            Every order this application has produced, whether it reached Zerodha or
            not — including the ones a risk rule refused to send.
          </p>
        </div>
        <div className="page-actions">
          <div className="chip-group">
            <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>
              All {all.length}
            </button>
            <button className={filter === "live" ? "on" : ""} onClick={() => setFilter("live")}>
              Live
            </button>
            <button className={filter === "paper" ? "on" : ""} onClick={() => setFilter("paper")}>
              Paper
            </button>
            <button
              className={filter === "problems" ? "on" : ""}
              onClick={() => setFilter("problems")}
            >
              Problems {problems > 0 ? problems : ""}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        {rows.length === 0 ? (
          <div className="empty">
            <strong>No orders</strong>
            {filter === "all"
              ? "An order appears here the moment a strategy acts on a signal."
              : "Nothing matches that filter."}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Placed</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th className="num">Qty</th>
                  <th className="num">Price</th>
                  <th className="num">Filled at</th>
                  <th className="num">Value</th>
                  <th>Status</th>
                  <th>Mode</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const bad = ["REJECTED", "REJECTED_LOCAL"].includes(o.status);
                  const value = (o.averagePrice ?? o.price ?? 0) * o.quantity;
                  return (
                    <tr key={o.id}>
                      <td className="small faint nowrap">
                        {clockFromMs(new Date(o.placedAt).getTime())}
                      </td>
                      <td>
                        <div className="sym">{o.tradingsymbol}</div>
                        <div className="sub">{o.exchange} · {o.product}</div>
                      </td>
                      <td>
                        <span className={`pill ${o.side === "BUY" ? "buy" : "sell"}`}>{o.side}</span>
                      </td>
                      <td className="num">
                        {o.quantity}
                        {o.filledQuantity > 0 && o.filledQuantity !== o.quantity && (
                          <div className="sub">{o.filledQuantity} filled</div>
                        )}
                      </td>
                      <td className="num">{o.price ? fmtPrice(o.price) : o.orderType}</td>
                      <td className="num">{o.averagePrice ? fmtPrice(o.averagePrice) : "—"}</td>
                      <td className="num">{value ? rupees(value) : "—"}</td>
                      <td>
                        <span className={`pill ${bad ? "danger" : o.status === "COMPLETE" ? "live" : "off"}`}>
                          {ORDER_CAPTION[o.status] ?? o.status}
                        </span>
                        {/* Zerodha's own words, not a summary of them. */}
                        {o.error && (
                          <div
                            className="tiny down"
                            style={{ maxWidth: 320, whiteSpace: "normal", marginTop: 3 }}
                          >
                            {o.error}
                          </div>
                        )}
                      </td>
                      <td><span className={`pill ${o.mode}`}>{o.mode}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
